import {
    Avatar,
    IconButton,
    InputAdornment,
    TextField,
    TextFieldProps,
} from '@mui/material'
import { UploadCloudIcon, XIcon } from 'lucide-react'
import React, { ChangeEvent, Fragment, useRef, useState } from 'react'
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form'
import { DialogFile } from './DialogFile'

type FormsProps = Omit<UseControllerProps, 'control'> &
    Omit<TextFieldProps, 'name' | 'value' | 'ref'> & {
        name: string
        notPreview?: boolean
        unHelperText?: boolean
        clearable?: boolean
        accept?: File['type'] | Array<File['type']>
    }

export const FormFile: React.FC<FormsProps> = ({
    name,
    rules,
    onClick,
    onChange,
    disabled: disableFile,
    color = 'info',
    size = 'medium',
    variant = 'filled',
    defaultValue,
    notPreview,
    unHelperText,
    clearable,
    accept,
    ...rest
}) => {
    const placeholder = disableFile
        ? ''
        : defaultValue || 'Selecione um arquivo'

    const { control, setValue, setError, clearErrors } = useFormContext()
    const disabled = disableFile || (control as any)._options?.disabled

    const [fileName, setFileName] = useState(placeholder)
    const [url, setUrl] = useState<string | null>(null)

    const setName = (value: string | File) => {
        if (value instanceof File) return value?.name
        else return value
    }

    const RefhiddenFileInput = useRef<HTMLInputElement>(null)
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (disabled) return
        if (!event.target.files) return
        const fileUploaded: File = event.target.files[0]

        if (accept) {
            const acceptArray = Array.isArray(accept) ? accept : [accept]
            if (!acceptArray.includes(fileUploaded.type)) {
                return setError(name, {
                    type: 'accept',
                    message: `Tipo de arquivo não permitido. Permitido: ${acceptArray.join(
                        ', ',
                    )}`,
                })
            }
        }

        clearErrors(name)

        setUrl(URL.createObjectURL(fileUploaded))
        setFileName(fileUploaded.name)
        setValue(name, fileUploaded)

        onChange?.(event)
    }

    const handleClick = (event: any) => {
        if (disabled) return
        if (RefhiddenFileInput.current) {
            RefhiddenFileInput.current.click()
        }

        if (onClick) {
            onClick(event)
        }
    }

    const StartAdornament: React.FC = () => {
        return (
            <InputAdornment position="start">
                <UploadCloudIcon />
            </InputAdornment>
        )
    }

    const [openDialog, setOpenDialog] = useState(false)

    const handleClose = () => {
        setOpenDialog(false)
    }

    const toggleDialogImage = () => {
        setOpenDialog(prev => !prev)
    }

    const EndAdornament = ({ value }: { value?: string }) => {
        if (!value) return null

        const uploadUrl = `${import.meta.env.VITE_BASE_URL}/v1/uploads/${value}`

        const handleClear = (e: any) => {
            e.preventDefault()
            setValue(name, '')
            setUrl(null)
            if (RefhiddenFileInput.current?.value)
                RefhiddenFileInput.current.value = ''
            onChange?.(e)
            e.stopPropagation()
        }

        return (
            <InputAdornment position="start">
                {!notPreview && (
                    <div
                        className="mb-2 size-8 cursor-pointer"
                        onClick={toggleDialogImage}
                    >
                        <Avatar
                            src={url || uploadUrl}
                            variant="square"
                            alt="end-adornament"
                            sx={{ width: '2rem', height: '2rem' }}
                        />
                    </div>
                )}
                {clearable && (
                    <IconButton title="Limpar" onClick={handleClear}>
                        <XIcon />
                    </IconButton>
                )}
            </InputAdornment>
        )
    }

    return (
        <Fragment>
            <input
                type="file"
                ref={RefhiddenFileInput}
                onChange={handleChange}
                style={{ display: 'none' }}
            />
            <Controller
                name={name}
                rules={rules}
                control={control}
                render={({
                    field: { value, ...field },
                    fieldState: { error },
                }) => (
                    <>
                        <TextField
                            {...rest}
                            {...field}
                            color={color}
                            defaultValue={fileName}
                            disabled={disabled}
                            error={!!error}
                            fullWidth
                            onClick={handleClick}
                            size={size}
                            value={setName(value)}
                            variant={variant}
                            InputProps={{
                                startAdornment: <StartAdornament />,
                                endAdornment: <EndAdornament value={value} />,
                            }}
                            helperText={
                                error && !unHelperText
                                    ? error.message
                                    : rest?.helperText
                                      ? rest.helperText
                                      : unHelperText
                                        ? null
                                        : ' '
                            }
                        />
                        {disabled && (
                            <DialogFile
                                image={value}
                                open={openDialog}
                                onClose={handleClose}
                            />
                        )}
                    </>
                )}
            />
        </Fragment>
    )
}
